import React, { Fragment } from "react";

const StaticFAQ = () => {
  return (
    <Fragment>
      <h1>FAQ (Frequently Asked Questions)</h1>
      <h3>Beach Report Card (BRC)</h3>
      <ul>
        <li>
          <a href="#brc">What is the Beach Report Card?</a>
        </li>
        <li>
          <a href="#waterquality">Why should I care about water quality?</a>
        </li>
        <li>
          <a href="#swim">
            Will I definitely get sick if I swim at an D or F beach?
          </a>
        </li>
        <li>
          <a href="#grades">How are the grades determined?</a>
        </li>
        <li>
          <a href="#update">How often are the grades updated?</a>
        </li>
        <li>
          <a href="#indicatorbacteria">What do the indicator bacteria mean?</a>
        </li>
        <li>
          <a href="#bacteria">How is the bacteria getting into the water?</a>
        </li>
        <li>
          <a href="#drywet">Why are there dry grades and wet grades?</a>
        </li>
        <li>
          <a href="#clean">What's being done to clean up polluted beaches?</a>
        </li>
        <li>
          <a href="#stayhealthy">
            How can I and my family stay healthy at the beach?
          </a>
        </li>
      </ul>
      <h3>NowCast</h3>
      <ul className="anchors">
        <li>
          <a href="#wqn_what">What is a Water Quality NowCast?</a>
        </li>
        <li>
          <a href="#wqn_many">
            How many beaches are involved in the NowCast program?
          </a>
        </li>
        <li>
          <a href="#wqn_where">
            Where and when will I be able to find the Water Quality NowCasts?
          </a>
        </li>
        <li>
          <a href="#wqn_who">Who developed the Predictive Models?</a>
        </li>
        <li>
          <a href="#wqn_difference">
            How are the daily NowCast from the models different from Heal the
            Bay's Beach Report Card grades?
          </a>
        </li>
        <li>
          <a href="#wqn_why">
            Why do we need predictive models for beach water quality?
          </a>
        </li>
        <li>
          <a href="#wqn_factors">What factors affect beach water quality?</a>
        </li>
        <li>
          <a href="#wqn_accuracy">How Accurate are the NowCast Models?</a>
        </li>
        <li>
          <a href="#wqn_shortcomings">
            Are there shortcomings to using NowCast models to make beach
            management decisions?
          </a>
        </li>
        <li>
          <a href="#wqn_elsewhere">
            Are predictive models being used at other beaches?
          </a>
        </li>
        <li>
          <a href="#wqn_no_prediction">
            My beach had a NowCast prediction yesterday, but there isn’t one
            today. What is going on?
          </a>
        </li>
      </ul>
      <a name="brc"></a>
      <p>
        <strong>What is the Beach Report Card?</strong>
        <br />
        Heal the Bay’s Beach Report Card is the only comprehensive analysis of
        coastline water quality in California. We monitor more than 650 beaches
        weekly from Washington to the Mexico border during the peak beach-going
        season (April-October), with approximately 300 locations in California
        from November-March. Each location is assigned an A to F grade based on
        the health risks of swimming, surfing and entering the water at that
        location. <br />
      </p>
      <a name="waterquality"></a>
      <p>
        <strong>Why should I care about water quality?</strong>
        <br />
        Numerous studies have shown that swimming in water with high levels of
        bacteria can make you seriously ill. The water may look fine, but it
        could be teeming with microorganisms that can cause severe stomach flu,
        respiratory illness and debilitating ear, nose and throat infections.
        <br />
      </p>
      <a name="swim"></a>
      <br />
      <p>
        <strong>
          Will I definitely get sick if I swim at an D or F beach?
        </strong>
        <br />
        No one can say for sure, but you definitely raise your risk. One in 25
        beachgoers will get sick swimming or surfing in polluted water near a
        flowing storm drain. Many D and F beaches are near these outfalls. Other
        poorly performing beaches are frequently near piers or in enclosed
        marinas and harbors with poor circulation. Simply put, the lower the
        grade, the greater the risk of getting sick.
        <br />
      </p>
      <a name="grades"></a>
      <p>
        <strong>How are the grades determined?</strong>
        <br />
        All county health departments are required to test beach water quality
        samples for three types of indicator bacteria at least once a week
        during the peak summer season (April-October). Heal the Bay compiles the
        complex shoreline data, analyzes it, and assigns an easy-to-understand
        letter grade.
        <br />
      </p>
      <a name="update"></a>
      <p>
        <strong>How often are the grades updated?</strong>
        <br />
        We publish new grades every Friday based on the latest data. These are
        available on this website. We also publish{" "}
        <a
          href="https://healthebay.org/beach-report-card-nowcast-2018/"
          target="_blank">
          Annual Reports
        </a>{" "}
        that provide detailed summary analyses for each coastal county, as well
        as a Summer Report Card that analyzes sites during the traditional
        beach-going season between Memorial Day and Labor Day.{" "}
      </p>
      <a name="gradeperiod"></a>
      <p>
        <strong>
          <em>Grade Period for Each Report</em>
        </strong>
        <br />
        The grade period is the total time from which water quality data is used
        to calculate dry and wet weather Beach Report Card grades. The Grade
        Period varies according to the type of Beach Report Card as follows:
      </p>
      <ul type="disc">
        <li>
          Weekly Report Cards: 4-week Grade Period
          <br />
          These grades are calculated weekly from a previous 4-week rolling
          average of data and are published every Friday in the Weekly Beach
          Report Card.
        </li>
        <li>
          Summer Report Cards: 3-month Grade Period
          <br />
          These grades are calculated from data collected during a 3-month
          period from Memorial Day (in May) through Labor Day (in September) of
          the given year. These grades are published every October in the Summer
          Beach Report Card.
        </li>
        <li>
          Annual Report Cards: 12-month Grade Period
          <br />
          These grades are calculated from data collected during a 12-month
          period from April 1 through March 31 of the following year. These
          grades are published every May in the Annual Beach Report Card.
        </li>
      </ul>
      <a name="indicatorbacteria"></a>
      <p>
        <strong>What do the indicator bacteria mean?</strong>
        <br />
        Indicator bacteria are not necessarily pathogenic, i.e. their presence
        will not definitely make you sick. However, numerous epidemiological
        studies have shown that these bacteria are usually found near
        microorganisms that do cause human illness. Unfortunately, there are no
        reliable water quality tests that can identify with certainty the
        pathogens themselves.
        <br />
      </p>
      <a name="bacteria"></a>
      <p>
        <strong>How is the bacteria getting into the water?</strong>
        <br />
        Determining the exact cause of bacterial pollution at a particular site
        can be a challenge. Sometimes the bacteria is the result of a leaky
        septic system nearby or a cracked sewer pipe. Other times, urban runoff
        carries animal droppings directly into the ocean via the storm drain
        system. Some unscrupulous boat owners discharge raw waste into the open
        ocean as well.
        <br />
      </p>
      <a name="drywet"></a>
      <br />
      <p>
        <strong>Why are there dry grades and wet grades?</strong>
        <br />
        The Beach Report Card provides grades for both dry and wet weather to
        allow a clear analysis of the water quality at any given monitoring
        location. Water quality significantly drops during and immediately after
        a rainstorm, but often rebounds to previous levels within a few days.
        Grades for dry weather are calculated for days of no rain and at least 3
        days after it stops raining. Grades for wet weather pertain to data from
        samples collected on days that experience rain, including the following
        three days. A rain event is any occurrence of rain greater than or equal
        to 0.10 inches for a given county. 
        <br />
      </p>
      <a name="clean"></a>
      <p>
        <strong>What's being done to clean up polluted beaches?</strong>
        <br />
        Due in large part to Heal the Bay’s Beach Report Card program and
        advocacy efforts, the state of California has committed over $100
        million to clean up its most polluted beaches. Under the Clean Beach
        Initiative, a series of funding measures has already paid for numerous
        runoff diversions and other infrastructure enhancements throughout the
        state.
        <br />
      </p>
      <a name="stayhealthy"></a>
      <br />
      <p>
        <strong>How can I and my family stay healthy at the beach?</strong>
      </p>
      <ul type="disc">
        <li>
          Water quality is generally very good during the dry summer months
          (April to October)
        </li>
        <li>Wait at least 72 hours after rainfall before you go swimming</li>
        <li>
          Swim 100 yards away from flowing storm drain outlets (imagine the
          length of a football field)
        </li>
        <li>Do not let children play in storm drains or puddles nearby</li>
        <li>
          Avoid enclosed beaches with poor water circulation – typically
          lagoons, marinas, “baby beaches” and harbors
        </li>
        <li>
          • Access the Beach Report Card to find out if your beach is safe
        </li>
      </ul>
      <p>
        <strong>What is the NowCast system?</strong>
        <br />
        The California Water Quality NowCast system is a tool that provides
        information similar to a daily weather forecast except the NowCast
        system predicts good or poor water quality for the day at select beaches
        across California. NowCast predictions are made using statistical
        computer models that estimate fecal bacteria levels in the surf zone.
        Predictions are made using up-to-date environmental information, and are
        released by 7AM every morning. Local agencies can compare the model
        results to the State’s bacteria health standards for contact with ocean
        waters to determine if the water is safe for recreational uses (such as
        swimming and surfing). When a model estimates that bacteria levels at a
        beach comply with the health standards, the NowCast result is shown as
        “Good”; however, if a model estimates that bacteria levels exceed health
        standards, the NowCast result is shown as “Poor”. 
        <br />
      </p>
      <a name="wqn_many"></a>
      <p>
        <strong>How many beaches are involved in the NowCast program?</strong>
        <br />
        As of 2017, ten beaches across southern California have summertime
        NowCast models making daily predictions:
      </p>
      <ul type="disc">
        <li>Cowell Beach [west of the wharf] (Santa Cruz)</li>
        <li>Arroyo Burro/&lsquo;Hendry&rsquo;s&rsquo; (Santa Barbara)</li>
        <li>East Beach at Mission Creek (Santa Barbara)</li>
        <li>Santa Monica Pier</li>
        <li>Redondo Beach Pier</li>
        <li>Long Beach City Beach [near 5th Place]</li>
        <li>Belmont Pier (Long Beach)</li>
        <li>Huntington State Beach [near Brookhurst Ave.]</li>
        <li>Doheny State Beach (Orange County)</li>
        <li>Moonlight Beach (Encinitas)</li>
      </ul>
      <p>
        In 2018, we plan to have 20 beaches in the NowCast system during summer,
        and 5 additional beaches in the NowCast system during the following
        winter.
        <br />
      </p>
      <a name="wqn_where"></a>
      <p>
        <strong>
          Where and when will I be able to find NowCast predictions?
        </strong>
        <br />
        Beachgoers can access NowCast predictions on Heal the Bay’s Beach Report
        Card website (beachreportcard.org) or on the Beach Report Card app,
        coming to iOS and Android devices in summer 2018. Sign up to receive an
        email alert when our new app goes live. Information and links to the
        predictions may also be available on participating city and county
        health agency websites.
      </p>
      <p>
        Daily predictions are currently being made for the duration of the State
        AB 411 summer season (April 1st through October 31st), though eventually
        models will be created to make wintertime predictions.
        <br />
      </p>
      <a name="wqn_who"></a>
      <p>
        <strong>Who developed the predictive models?</strong>
        <br />
        Our predictive models are developed as part of a collaboration between
        Heal the Bay, Stanford University and UCLA, and are funded by the
        California State Water Resources Control Board.
      </p>
      <p>
        Initial research was performed from 2012-2015. Experts in beach water
        quality developed and tested over 700 different beach models using many
        years of historical data on environmental conditions and bacteria levels
        from 25 beaches in California. This study represents the most
        comprehensive study completed to date on using predictive models at
        beaches. Following the study, a successful pilot test was performed at
        three California beaches during the summer of 2015 to determine if a
        nowcasting program in the state could effectively provide water quality
        information to the public on a daily basis.
        <br />
      </p>
      <a name="wqn_difference"></a>
      <p>
        <strong>
          How are the daily NowCast predictions from the models different from
          Heal the Bay’s Beach Report Card grades?
        </strong>
        <br />
        NowCast models for a given beach take into account current environmental
        conditions and historic bacteria levels to predict fecal indicator
        bacteria levels on a daily basis. The Beach Report Card provides weekly
        grades every Friday throughout the year based on the last 30 days of
        sample data, including the current week. The grades take into
        consideration the magnitude and frequency of exceedances above allowed
        bacterial levels over the course of the specified time period. So, while
        the NowCast models predict current conditions at the beach every
        morning, the Beach Report Card grades take a longer look back in time to
        look at the average conditions. Both pieces of information are important
        to a beach-goer assessing beach water quality because fecal bacteria
        levels can vary considerably over short time periods. The NowCast system
        and Heal the Bay‘s Beach Report Card are valuable tools to help people
        protect themselves and their family and friends.
        <br />
      </p>
      <a name="wqn_why"></a>
      <br />
      <p>
        <strong>
          Why do we need predictive models for beach water quality?
        </strong>
        <br />
        Currently, local health agencies use laboratory analyses of water
        samples collected at the beach to determine if it is safe for
        recreational use. Unfortunately, there is a long delay in this approach:
        it typically takes 24-48 hours to collect the samples, transport them to
        the lab, and analyze them. Meanwhile, water quality can change with
        environmental conditions. Additionally, most California beaches are
        sampled on a weekly basis (although there are some beaches that are
        monitored more frequently). As a result, health agencies currently rely
        on data that is days-old to make health protection decisions. Predictive
        models can quickly and accurately provide daily water quality
        information based on the most recent environmental conditions at the
        beach. Local health agencies can then make public notifications of poor
        water quality in the morning before most people arrive at the beach.
        <br />
      </p>
      <a name="wqn_factors"></a>
      <br />
      <p>
        <strong>What factors affect beach water quality?</strong>
        <br />
        Fecal bacteria levels can be affected by many environmental factors such
        as rainfall, tide levels, solar radiation, wind, and wave action. Water
        quality can even be affected by human-made factors such storm drain flow
        and the presence of piers or jetties. Because the effects of these
        factors on water quality varies from beach to beach, site-specific
        NowCast models are developed for individual beaches. 
        <br />
      </p>
      <a name="wqn_accuracy"></a>
      <p>
        <strong>How accurate are the NowCast models?</strong>
        <br />
        Models are created and calibrated to be more accurate than the current
        method of using days-old sample data to make a decision on whether to
        post warning signage at the beach or not. In practice, the predictive
        models have generally performed as well as – or better than – the
        current method. For example, during the summer swim season of 2017, the
        models accurately predicted a median 50% of the days when beach water
        quality failed to meet the health standards, compared to a median 0%
        from the current method. 
        <br />
      </p>
      <a name="wqn_shortcomings"></a>
      <p>
        <strong>
          Are there shortcomings to using NowCast models to make beach
          management decisions?
        </strong>
        <br />
        Predictive models are developed using the best available environmental
        data to capture variations in beach water quality. However, not all
        factors that affect water quality have available data to use in
        modeling. For example, the presence of seagulls at a beach has been
        linked to increased indicator bacteria level, yet accurate seagull
        counts at the time of water sampling are typically unavailable at this
        time. The models are also unable to predict acute pollution events like
        sewage and oil spills.
        <br />
      </p>
      <a name="wqn_elsewhere"></a>
      <p>
        <strong>Are predictive models being used at other beaches?</strong>
        <br />
        Yes. In the U.S., predictive models are implemented in the summertime at
        several beaches in the Great Lakes, including beaches in Ohio, New York,
        Wisconsin and Illinois. At these beaches, local beach agencies
        successfully use predictive models to determine when the public should
        be notified of bacteria levels that exceed health standards. Predictive
        models are also used at Hong Kong coastal beaches where the predictive
        models accurately predict 30-70% of water quality standard exceedances,
        and have been shown to reduce 27% of illnesses caused by swimming in
        contaminated beach waters. Other locations that have research predictive
        models for beach water quality include Puerto Rico, New Zealand,
        Germany, the UK, South Korea, and Croatia.
        <br />
      </p>
      <a name="wqn_no_prediction"></a>
      <br />
      <p>
        <strong>
          My beach had a NowCast prediction yesterday, but there isn’t one
          today. What is going on?
        </strong>
        <br />
        There are a couple of reasons for a missing NowCast prediction at a
        beach that is in the NowCast system. New data is posted by 7AM Pacific
        Time every morning, so it could be that the daily predictions have
        simply not been made yet. However, if no NowCast prediction is available
        later in the day, data analysis may have been postponed due to
        insufficient environmental or bacterial data, or technical difficulties
        with our server. Finally, NowCast predictions for certain beaches are
        only available on a seasonal basis. If the current swim season has
        ended, the NowCast models for the beach are being analyzed and re-tuned
        for use next season.
        <br />
      </p>
      <p>
        Please check back the next day for an updated water quality NowCast
        prediction, or use the next best thing – the Beach Report Card grade.
      </p>
      <p>
        While every effort will be made to ensure accurate information
        dissemination, the project team (Stanford University, UCLA, Heal the
        Bay) and any involved health agencies and departments make no
        representation about the content for any purpose, and assume no
        responsibility for anyone’s use of the information.
      </p>
    </Fragment>
  );
};

export default StaticFAQ;



// WEBPACK FOOTER //
// ./src/components/static/StaticFAQ.js